/**
 * News Filters
 * Handles filtering, sorting, and pagination for news articles
 * Cherry-picked from example-filters.js for news-specific functionality
 */

const _debounce = require('lodash.debounce');

window.addEventListener('load', () => {
    const limit = 12;
    let offset = 0;

    const filterContainers = document.querySelectorAll('.filters-container');

    for (const filterContainer of filterContainers) {
        const filterParentSection = filterContainer.closest('.news-filter-panel');
        if (!filterParentSection) continue; // Only process news filter panels

        const filterCardParentSection = filterParentSection?.nextElementSibling;
        const allFilters = [...filterContainer.querySelectorAll('select,input')];
        const allNewsCards = filterCardParentSection ? [...filterCardParentSection.querySelectorAll('.filterable-card')] : [];
        const clearButton = filterContainer.querySelector('button.clear-button');
        const cardContainer = allNewsCards?.[0]?.parentElement;
        const defaultFilterValues = {};

        const updateClearButtonState = () => {
            const isDefault = allFilters.every((filterElement) => {
                const key = filterElement.getAttribute('data-for');
                if (!key) return true;
                if (filterElement.type === 'checkbox') return !filterElement.checked;
                return filterElement.value === (defaultFilterValues[key] ?? '');
            });
            clearButton?.classList.toggle('disabled', isDefault);
        };

        const loadMore = (event) => {
            // Only increment offset if this is a button click (not initial load)
            if (event) {
                offset += limit;
            }

            const nonFilteredNewsCards = filterCardParentSection?.querySelectorAll('.filterable-card[data-filtered="false"]');
            const firstCard = filterCardParentSection?.querySelector('.filterable-card[data-filtered="false"]');
            const startAtIndex = nonFilteredNewsCards ? [...nonFilteredNewsCards].indexOf(firstCard) : -1;

            if (nonFilteredNewsCards && (startAtIndex !== -1)) {
                nonFilteredNewsCards.forEach((cardElement, index) => {
                    if (index >= startAtIndex && index < (offset + limit + startAtIndex)) {
                        cardElement.setAttribute('data-loaded', 'true');
                    }
                });
            }

            const loadMoreButton = event ? event.currentTarget : filterCardParentSection?.querySelector('button.load-more');
            const shownCards = filterCardParentSection ? filterCardParentSection.querySelectorAll('.filterable-card[data-filtered="false"]').length : 0;
            const loadedCards = filterCardParentSection ? filterCardParentSection.querySelectorAll('.filterable-card[data-loaded="true"]').length : 0;

            if (loadMoreButton) {
                if (loadedCards < shownCards) {
                    loadMoreButton.classList.remove('hidden');
                } else {
                    loadMoreButton.classList.add('hidden');
                }
            }
        };

        const resetLoadedItems = () => {
            offset = 0;
            const loadedCards = filterCardParentSection?.querySelectorAll('[data-loaded]');
            if (loadedCards) {
                loadedCards.forEach((card) => {
                    card.setAttribute('data-loaded', 'false');
                });
            }
        };

        const onFiltersChanged = (event) => {
            resetLoadedItems();

            const filterValues = [...allFilters].reduce((carry, filterElement) => {
                const dataFor = filterElement.getAttribute('data-for');
                const output = { ...carry };

                if (dataFor) {
                    if (filterElement instanceof HTMLSelectElement) {
                        output[dataFor] = filterElement.value;
                    } else if (filterElement instanceof HTMLInputElement) {
                        switch (filterElement.getAttribute('type')) {
                            case 'checkbox':
                                output[dataFor] = output?.[dataFor] ?? [];
                                if (filterElement.checked) {
                                    output[dataFor].push(filterElement.value);
                                }
                                break;
                            default:
                                output[dataFor] = filterElement.value;
                        }
                    }
                }

                return output;
            }, {});

            allNewsCards.forEach((newsCard) => {
                let cardHidden = false;

                for (let key in filterValues) {
                    const dataAttrib = `data-${key.toLowerCase().replace(' ', '-')}`;

                    if (newsCard.hasAttribute(dataAttrib)) {
                        let cardValue = newsCard.getAttribute(dataAttrib);
                        let filterValue = filterValues[key];

                        switch (key) {
                            // Skip at this stage
                            case 'Sort':
                                break;

                            // Title search
                            case 'Title':
                                if (cardValue.toLowerCase().indexOf(filterValue.toLowerCase()) === -1) {
                                    cardHidden = true;
                                }
                                break;

                            case 'Tags':
                                // OR logic: show if card has ANY of the selected tags (case-insensitive)
                                if (filterValue && filterValue.length > 0) {
                                    cardValue = cardValue.split(',').map(v => v.trim().toLowerCase());
                                    let hasMatchingTag = false;

                                    for (let tag of filterValue) {
                                        if (cardValue.indexOf(tag.toLowerCase()) !== -1) {
                                            hasMatchingTag = true;
                                            break;
                                        }
                                    }

                                    if (!hasMatchingTag) {
                                        cardHidden = true;
                                    }
                                }
                                break;

                            case 'Author':
                                // Simple string comparison for author (case-insensitive)
                                if (filterValue !== 'Any' && filterValue.toString().toLowerCase() !== cardValue.toString().toLowerCase()) {
                                    cardHidden = true;
                                }
                                break;

                            case 'Year':
                                // Filter by year - exact match
                                if (filterValue !== 'Any' && filterValue.toString() !== cardValue.toString()) {
                                    cardHidden = true;
                                }
                                break;

                            default:
                                if (filterValue !== 'Any' && filterValue.toString() !== cardValue.toString()) {
                                    cardHidden = true;
                                }
                                break;
                        }
                    }
                }

                newsCard.setAttribute('data-filtered', cardHidden.toString());
            });

            // Sort the cards
            allNewsCards.sort((cardElementA, cardElementB) => {
                const titleA = cardElementA.getAttribute('data-title');
                const titleB = cardElementB.getAttribute('data-title');

                switch (filterValues['Sort']) {
                    case 'A - Z':
                        return titleA.localeCompare(titleB);

                    case 'Z - A':
                        return titleB.localeCompare(titleA);

                    case 'Most Recent':
                        const timestampA = cardElementA.getAttribute('data-posted');
                        const timestampB = cardElementB.getAttribute('data-posted');

                        if (timestampA === timestampB) {
                            const idA = cardElementA.getAttribute('data-id');
                            const idB = cardElementB.getAttribute('data-id');
                            return idB - idA;
                        } else {
                            return timestampB - timestampA;
                        }

                    case 'Oldest':
                        const timestampAOldest = cardElementA.getAttribute('data-posted');
                        const timestampBOldest = cardElementB.getAttribute('data-posted');

                        if (timestampAOldest === timestampBOldest) {
                            const idA = cardElementA.getAttribute('data-id');
                            const idB = cardElementB.getAttribute('data-id');
                            return idA - idB;
                        } else {
                            return timestampAOldest - timestampBOldest;
                        }
                }

                return 0;
            });

            // Re-append cards in sorted order
            allNewsCards.forEach((cardElement) => {
                cardContainer.appendChild(cardElement);
            });

            // Update multi-select labels
            allFilters.filter((filterElement) => filterElement instanceof HTMLSelectElement && filterElement.classList.contains('multi-select-filter')).forEach((filterElement) => {
                const parentDiv = filterElement.closest('.relative');
                const firstCheckbox = parentDiv?.querySelector('input[type="checkbox"][data-for]');
                const filterName = firstCheckbox?.getAttribute('data-for');

                if (filterName && filterValues[filterName]) {
                    filterElement.firstElementChild.innerText = filterValues[filterName].length ? `${filterValues[filterName].length} Selected` : 'Any';
                }
            });

            // Update count display
            const cardTitleDiv = filterParentSection?.querySelector('.card-filter-title');
            const shownCards = filterCardParentSection ? filterCardParentSection.querySelectorAll('.filterable-card[data-filtered="false"]').length : 0;
            const entryCount = cardTitleDiv?.querySelector('.entry-count');
            const entryType = cardTitleDiv?.querySelector('.entry-type');
            if (entryCount) {
                entryCount.innerHTML = shownCards.toString();
            }
            if (entryType) {
                entryType.innerHTML = shownCards === 1 ? cardTitleDiv.getAttribute('data-singular-title') : cardTitleDiv.getAttribute('data-plural-title');
            }

            // Show/hide no results message
            const noResultsMessage = filterCardParentSection?.querySelector('.no-results-message');
            const cardGrid = filterCardParentSection?.querySelector('.grid');
            if (noResultsMessage && cardGrid) {
                if (shownCards === 0) {
                    noResultsMessage.classList.remove('hidden');
                    cardGrid.classList.add('hidden');
                } else {
                    noResultsMessage.classList.add('hidden');
                    cardGrid.classList.remove('hidden');
                }
            }

            loadMore();
            updateClearButtonState();
        };

        // Clear button handler
        clearButton?.addEventListener('click', () => {
            allFilters.forEach((filterElement) => {
                const key = filterElement.getAttribute('data-for');
                if (defaultFilterValues.hasOwnProperty(key)) {
                    filterElement.value = defaultFilterValues[key];
                }

                if (filterElement.type === 'checkbox') {
                    filterElement.checked = false;
                }
            });

            onFiltersChanged();
            updateClearButtonState();
        });

        // Close multi-select when clicking outside
        document.addEventListener('click', (event) => {
            const source = event.target;
            const isInsideMultiselect = !!source.closest('.multi-select-content');

            if (!isInsideMultiselect) {
                filterContainer.querySelectorAll('.multi-select-content[data-open="true"]').forEach((element) => {
                    element.classList.remove('opacity-100');
                    element.classList.remove('pointer-events-auto');
                    element.setAttribute('data-open', 'false');
                });
            }
        });

        // Bind filter events
        allFilters.forEach((filterElement) => {
            filterElement.addEventListener('change', onFiltersChanged);

            if (filterElement instanceof HTMLInputElement && filterElement.type === 'text') {
                filterElement.addEventListener('input', _debounce(onFiltersChanged, 500));
            }

            if (filterElement.classList.contains('multi-select-filter')) {
                const clickElement = filterElement.parentElement.querySelector('.multi-select-click');
                const contentElement = filterElement.parentElement.querySelector('.multi-select-content');

                clickElement?.addEventListener('click', (event) => {
                    event.preventDefault();
                    event.stopPropagation();

                    const isCurrentlyOpen = contentElement.getAttribute('data-open') === 'true';

                    contentElement.classList.toggle('opacity-100', !isCurrentlyOpen);
                    contentElement.classList.toggle('pointer-events-auto', !isCurrentlyOpen);
                    contentElement.setAttribute('data-open', (!isCurrentlyOpen).toString());
                });
            }

            if (filterElement.type !== 'checkbox') {
                const dataFor = filterElement.getAttribute('data-for');

                if (dataFor) {
                    defaultFilterValues[filterElement.getAttribute('data-for')] = filterElement.value;
                }
            }
        });

        // Read URL parameters and set filter values
        const urlParams = new URLSearchParams(window.location.search);
        const tagParam = urlParams.get('tag');

        if (tagParam) {
            const tagCheckboxes = filterContainer.querySelectorAll('input[type="checkbox"][data-for="Tags"]');
            tagCheckboxes.forEach((checkbox) => {
                if (checkbox.value === tagParam) {
                    checkbox.checked = true;
                }
            });
        }

        // Initial filter application
        onFiltersChanged();
        updateClearButtonState();

        // Bind load more button
        const loadMoreButton = filterCardParentSection?.querySelector('button.load-more');
        loadMoreButton?.addEventListener('click', loadMore);
    }
});

